'use client';

import { motion } from 'framer-motion';
import { CheckCircle2, ArrowRight, Calendar, Zap } from 'lucide-react';
import { Smartphone } from '@/lib/icons';
import { EASE_OUT, EASE_CUSTOM } from '@/lib/animations'

const plans = [
    {
        title: 'Premium',
        tag: 'Reunión personalizada',
        icon: Calendar,
        description: 'Videollamada de diagnóstico contigo. Te conocemos, analizamos tu negocio y diseñamos una propuesta a medida con ROI proyectado. Para proyectos donde el contexto requiere análisis profundo.',
        features: [
            'Videollamada de diagnóstico (45-60 min)',
            'Informe + propuesta personalizada en 48h',
            'ROI proyectado y plan por fases',
        ],
        popular: false,
        cta: 'Agendar diagnóstico',
        ctaLink: 'https://calendly.com/innovatheratech/30min',
        ctaExternal: true,
        price: 'Desde 1.200€',
        priceNote: 'según alcance',
    },
    {
        title: 'Express',
        tag: 'Sin esperas',
        icon: Zap,
        description: 'Ya sabes lo que necesitas. Rellena el formulario detallado, te enviamos propuesta en 24h por email. Sin reuniones obligatorias hasta que tú quieras.',
        features: [
            'Formulario detallado (~10 min)',
            'Propuesta por email en 24h',
            'Cierre directo cuando confirmes',
        ],
        popular: true,
        cta: 'Pedir presupuesto express',
        ctaLink: '#contacto',
        ctaExternal: false,
        price: 'Desde 1.200€',
        priceNote: 'según alcance',
    },
    {
        title: 'Asíncrono',
        tag: 'Sin reuniones',
        icon: Smartphone,
        description: 'Para quien valora su tiempo. Formulario + vídeo explicativo por WhatsApp con la estrategia y el presupuesto. Firmas y pagas online cuando quieras. Entrega por sprints semanales.',
        features: [
            'Formulario + vídeo explicativo por WhatsApp',
            'PDF firmable + pago online (Verifactu vía Holded)',
            'Sprints semanales con avance visible',
        ],
        popular: false,
        cta: 'Empezar sin reuniones',
        ctaLink: '#contacto',
        ctaExternal: false,
        price: 'Desde 1.200€',
        priceNote: 'por sprint',
    },
];

export default function ModelosColaboracion() {
    return (
        <section id="precios" className="py-20 md:py-32 relative z-10">
            <div className="max-w-7xl mx-auto px-6 lg:px-8">

                <div className="text-center mb-14">
                    <div className="text-xs font-mono tracking-[0.3em] text-slate-400 uppercase mb-4">
                        Tres formas de empezar
                    </div>
                    <h2 className="text-2xl sm:text-4xl md:text-5xl font-black tracking-tight text-slate-900 mb-4">
                        Sin tarifario.
                        <br />
                        <span className="text-slate-400 font-light">Tres formas de empezar.</span>
                    </h2>
                    <p className="text-lg text-slate-500 font-light max-w-xl mx-auto">
                        Cada propuesta se diseña según tu situación. Tú eliges cómo prefieres recibirla.
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                    {plans.map((plan, idx) => {
                        const Icon = plan.icon;
                        return (
                            <motion.div
                                key={plan.title}
                                initial={{ opacity: 0, y: 28 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: '-60px' }}
                                transition={{ duration: 0.65, delay: idx * 0.12, ease: EASE_OUT }}
                                className={`glass-card rounded-3xl p-8 flex flex-col relative transition-transform duration-300 hover:-translate-y-1 ${plan.popular ? 'ring-2 ring-blue-300/50' : ''}`}
                            >
                                {plan.popular && (
                                    <div className="absolute -top-px inset-x-0 h-0.5 bg-gradient-to-r from-transparent via-blue-500 to-transparent rounded-t-3xl" />
                                )}
                                <div className="flex items-start justify-between mb-3">
                                    <div className="flex items-center gap-2.5">
                                        <div className={`w-9 h-9 rounded-xl flex items-center justify-center border ${plan.popular ? 'bg-blue-50 border-blue-100' : 'bg-slate-50 border-slate-200/60'}`}>
                                            <Icon className={`w-4 h-4 ${plan.popular ? 'text-blue-600' : 'text-slate-500'}`} />
                                        </div>
                                        <h3 className="text-2xl font-black tracking-tight text-slate-900">{plan.title}</h3>
                                    </div>
                                    <span className={`text-xs font-mono px-2.5 py-1 rounded-lg border ${plan.popular ? 'bg-blue-50 border-blue-200 text-blue-700' : 'bg-slate-50 border-slate-200 text-slate-500'}`}>
                                        {plan.tag}
                                    </span>
                                </div>
                                <div className="mb-4">
                                    <span className="text-2xl font-black text-slate-900">{plan.price}</span>
                                    <span className="text-sm font-normal text-slate-400 ml-1.5">{plan.priceNote}</span>
                                </div>
                                <p className="text-slate-500 font-light text-sm leading-relaxed mb-6">{plan.description}</p>
                                <ul className="flex flex-col gap-3 mb-8 flex-grow">
                                    {plan.features.map(f => (
                                        <li key={f} className="flex items-start gap-2.5 text-sm text-slate-700">
                                            <CheckCircle2 className={`w-4 h-4 shrink-0 mt-0.5 ${plan.popular ? 'text-blue-500' : 'text-slate-400'}`} />
                                            {f}
                                        </li>
                                    ))}
                                </ul>
                                <a
                                    href={plan.ctaLink}
                                    target={plan.ctaExternal ? '_blank' : undefined}
                                    rel={plan.ctaExternal ? 'noopener noreferrer' : undefined}
                                    className={`w-full py-3.5 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 transition-all active:scale-95 ${plan.popular ? 'bg-blue-600 text-white hover:bg-blue-700 shadow-[0_4px_16px_rgba(37,99,235,0.3)]' : 'bg-slate-100 text-slate-700 hover:bg-slate-200 border border-slate-200'}`}
                                >
                                    {plan.cta}
                                    <ArrowRight className="w-4 h-4" />
                                </a>
                            </motion.div>
                        );
                    })}
                </div>

                {/* Acompañamiento mensual — add-on post-entrega */}
                <div className="mt-8 text-center">
                    <p className="text-xs font-mono tracking-widest text-slate-400 uppercase">
                        Acompañamiento mensual disponible post-entrega — desde 49€/mes · sin permanencia
                    </p>
                </div>

                <div className="mt-10 text-center">
                    <p className="text-sm text-slate-500">
                        ¿No sabes cuál te encaja?{' '}
                        <a href="#contacto"
                            className="text-blue-600 font-medium hover:underline">
                            Cuéntanos en el formulario y te orientamos.
                        </a>
                    </p>
                </div>
            </div>
        </section>
    );
}
